import { useEffect, useState } from 'react';
import { Eye, EyeOff, RefreshCcw, Save } from 'lucide-react';
import { EmptyState, LoadingRows } from '../components/StateViews';
import { fetchAdminData, updateCreationReview, updateSeriesVisibility } from '../lib/firestore';
import { formatTimestamp } from '../lib/format';
import type { AdminSeriesItem, CreationSubmission, VoteResponse } from '../types/content';

const statuses: CreationSubmission['status'][] = ['new', 'reviewing', 'approved', 'rejected'];

function SeriesVisibilityTable({ series, onSaved }: { series: AdminSeriesItem[]; onSaved: () => void }) {
  const [savingId, setSavingId] = useState<string | null>(null);

  async function toggleVisibility(item: AdminSeriesItem) {
    setSavingId(item.id);
    try {
      await updateSeriesVisibility(item.id, item.status !== 'published');
      onSaved();
    } finally {
      setSavingId(null);
    }
  }

  if (series.length === 0) return <p className="muted">No series found.</p>;

  return (
    <div className="admin-table-wrap">
      <table className="series-admin-table">
        <thead>
          <tr>
            <th>Poster</th>
            <th>Title</th>
            <th>Episodes</th>
            <th>Author</th>
            <th>Status</th>
            <th>Visible</th>
          </tr>
        </thead>
        <tbody>
          {series.map((item) => {
            const visible = item.status === 'published';
            return (
              <tr key={item.id}>
                <td>
                  <img src={item.posterUrl || item.bannerUrl || '/app_icon.png'} alt="" />
                </td>
                <td>
                  <strong>{item.title}</strong>
                  <span>{item.genres[0] || item.maturityRating || 'Series'}</span>
                </td>
                <td>{item.episodeCount}</td>
                <td>{item.author}</td>
                <td>
                  <span className={visible ? 'status-pill published' : 'status-pill hidden'}>{item.status}</span>
                </td>
                <td>
                  <button className="visibility-toggle" type="button" onClick={() => void toggleVisibility(item)} disabled={savingId === item.id}>
                    {visible ? <Eye size={16} /> : <EyeOff size={16} />}
                    {savingId === item.id ? 'Saving' : visible ? 'Shown' : 'Hidden'}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CreationRow({ item, onSaved }: { item: CreationSubmission; onSaved: () => void }) {
  const [status, setStatus] = useState<CreationSubmission['status']>(item.status);
  const [note, setNote] = useState(item.adminNote);
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await updateCreationReview(item.id, status, note);
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  return (
    <article className="admin-card">
      <div className="admin-card-head">
        <div>
          <p>{item.genre} / {item.format}</p>
          <h3>{item.title}</h3>
        </div>
        <span>{formatTimestamp(item.createdAt)}</span>
      </div>
      <p className="admin-body">{item.logline}</p>
      {item.referenceUrl && <a href={item.referenceUrl} target="_blank" rel="noreferrer">Reference</a>}
      <div className="review-controls">
        <select value={status} onChange={(event) => setStatus(event.target.value as CreationSubmission['status'])}>
          {statuses.map((entry) => <option key={entry}>{entry}</option>)}
        </select>
        <textarea value={note} onChange={(event) => setNote(event.target.value)} placeholder="Admin note" rows={2} />
        <button className="secondary-action" type="button" onClick={save} disabled={saving}>
          <Save size={16} /> {saving ? 'Saving' : 'Save'}
        </button>
      </div>
    </article>
  );
}

export function AdminPage() {
  const [creations, setCreations] = useState<CreationSubmission[]>([]);
  const [votes, setVotes] = useState<VoteResponse[]>([]);
  const [series, setSeries] = useState<AdminSeriesItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  async function load(showLoading = true) {
    if (showLoading) setLoading(true);
    setError(false);
    try {
      const data = await fetchAdminData();
      setCreations(data.creations);
      setVotes(data.votes);
      setSeries(data.series);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let mounted = true;
    fetchAdminData()
      .then((data) => {
        if (!mounted) return;
        setCreations(data.creations);
        setVotes(data.votes);
        setSeries(data.series);
      })
      .catch(() => {
        if (mounted) setError(true);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) return <div className="page-pad"><LoadingRows label="Loading admin dashboard" /></div>;
  if (error) return <div className="page-pad"><EmptyState title="Admin unavailable" body="Review data could not be loaded." /></div>;

  return (
    <div className="page-pad admin-page">
      <div className="section-heading admin-heading">
        <div>
          <p className="eyebrow">Admin</p>
          <h1>Control what ships.</h1>
        </div>
        <button className="icon-action" type="button" onClick={() => void load()} aria-label="Refresh">
          <RefreshCcw size={18} />
        </button>
      </div>

      <section>
        <h2>Series visibility</h2>
        <p className="muted">Toggle what appears in the app and website. Shown writes <strong>published</strong>; hidden writes <strong>hidden</strong>.</p>
        <SeriesVisibilityTable series={series} onSaved={() => void load(false)} />
      </section>

      <section>
        <h2>Creator submissions</h2>
        {creations.length === 0 ? <p className="muted">No submissions yet.</p> : creations.map((item) => <CreationRow key={item.id} item={item} onSaved={load} />)}
      </section>

      <section>
        <h2>Vote responses</h2>
        {votes.length === 0 ? (
          <p className="muted">No votes yet.</p>
        ) : (
          votes.map((item) => (
            <article className="admin-card" key={item.id}>
              <div className="admin-card-head">
                <div>
                  <p>{item.email}</p>
                  <h3>{item.feature}</h3>
                </div>
                <span>{formatTimestamp(item.updatedAt)}</span>
              </div>
              <p className="admin-body">{item.usage}</p>
              {item.suggestion && <p className="admin-body admin-quote">{item.suggestion}</p>}
            </article>
          ))
        )}
      </section>
    </div>
  );
}
